import ActionButton from '@able/react/dist/ActionButton';
import TextField from '@able/react/dist/TextField';
import { AbleCommonProps } from '../Types';
import { useEffect, useRef, useState } from 'react';
import NumberButton from './NumberButton';
import './style.scss';
import usePaginate, { PageProps } from './usePaginate';

export type PaginationProps = {
    id?: string;
    /** This will be attached to aria-label */
    label?: string;
    /** isJumpToPage prop is to show the jump to page section, default value is true */
    isJumpToPage?: boolean;
    paginationClassName?: string;
} & PageProps & AbleCommonProps;

/**
 * Pagination is a component can be used in Table or Individually
 *
 *
 * @version 1.0.0
 */

const Pagination = (props: PaginationProps) => {
    const jumpTextInputRef = useRef<HTMLInputElement>(null);
    const { developmentUrl, isJumpToPage = true, paginationClassName = '' } = props;
    const [jumpPageNumber, setJumpPageNumber] = useState<number | string>('');
    let mpc = 10;
    if (window.innerWidth > 1023) {
        mpc = 10;
    } else {
        mpc = 4
    }
    const [maxPageNumberCount, setMaxPageNumberCount] = useState<number>(mpc);
    const { id = 'ted-ui-pagination', page, totalRecords, pageSize = 10, onPageChange, label = "Pagination" } = props;

    const onResize = () => {
        mpc = maxPageNumberCount;
        if (window.innerWidth > 1023) {
            mpc = 10;
        } else {
            mpc = 4
        }
        setMaxPageNumberCount(mpc);
    }

    useEffect(() => {
        window.addEventListener('resize', onResize, false);
        return () => { window.removeEventListener('resize', onResize, false); }
    }, []);

    const { nextPage, previousPage, pages, nextPageDisabled, previousPageDisabled, pageNumbers, jumpToPage } = usePaginate({
        page,
        pageSize,
        type: 'complex',
        totalRecords,
        onPageChange,
        pageShowCount: maxPageNumberCount
    })

    const pageTextLabel = `${id}--page-text`;
    const pageTextLabelTotal = `${id}--pages-total`;

    const jump = (e: any) => {
        e.preventDefault();
        jumpToPage && jumpToPage(Number(jumpPageNumber));
        setJumpPageNumber('');
        if (jumpTextInputRef.current) {
            jumpTextInputRef.current.focus();
        }
    }

    const prevButtonLabel = previousPageDisabled ? 'you are on the first page' : `go to page ${page - 1}`;
    const nextButtonLabel = nextPageDisabled ? 'you are on the last page' : `go to page ${page + 1}`;

    const ariaAttrJumpBox: any = {
        "aria-describedby": pageTextLabelTotal,
        "inputMode": "numaric"
    }

    return <nav className={`ted-ui-pagination-complex ${paginationClassName ? `${paginationClassName}` : ''}`} aria-label={label} id={id}>

        <div className='ted-ui-page-section'>
            <NumberButton onClick={previousPage} aria-label={prevButtonLabel} data-testid="previousButton" icon='ChevronLeft' developmentUrl={developmentUrl} aria-disabled={previousPageDisabled} />

            <div className='pages'>
                <ul aria-label='page numbers'>
                    {pageNumbers.map(pageNumber => {
                        return <li key={pageNumber} ><NumberButton onClick={() => {
                            setJumpPageNumber('');
                            onPageChange && onPageChange(pageNumber)
                        }} number={pageNumber} aria-current={page === pageNumber ? 'page' : undefined} aria-label={`page ${pageNumber}`} /></li>
                    })}
                </ul>
            </div>

            <NumberButton onClick={nextPage} aria-label={nextButtonLabel} data-testid="nextButton" icon='ChevronRight' developmentUrl={developmentUrl} aria-disabled={nextPageDisabled} />
        </div>

        {isJumpToPage && <form className='ted-ui-pagination-page-info' onSubmit={jump}>
            <TextField ref={jumpTextInputRef} id={pageTextLabel} type='number' min={`1`} max={`${pages}`} value={jumpPageNumber as any} events={{ onChange: (e: any) => setJumpPageNumber(e.target.value) }} name={'pageTextLabel'} label='Jump to page' {...ariaAttrJumpBox} />
            <span id={pageTextLabelTotal}>of {pages}</span>
            <ActionButton type='submit' label='Go' element={'button'} className='ted-ui-jump-page-button' data-testid="jumpButton" variant={'MediumEmphasisCompact'} />
        </form>}
    </nav>;
}

export default Pagination;